import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stat, Loading } from "@/components/ui-kit";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Upload, FileClock, Send, Users, ArrowUpRight, AlertTriangle } from "lucide-react";

export default function TutorDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/tutor/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="animate-fade-up">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[#7C3AED]">{greet}</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#1E1B2E] sm:text-4xl">{user?.name}</h1>
          <p className="mt-1.5 text-[#524B6B]">Here's what's happening across your programs this week.</p>
        </div>
        <Link to="/tutor/upload">
          <Button data-testid="upload-cta" className="h-11 rounded-xl ember-gradient font-bold shadow-lg shadow-violet-500/25">
            <Upload className="mr-2 h-4 w-4" /> Upload Homework
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat testid="stat-uploaded" icon={Upload} label="Homework uploaded" value={data.uploaded} tone="violet" />
        <Stat testid="stat-awaiting" icon={FileClock} label="Awaiting approval" value={data.awaiting} tone="amber" sub="Ready for review" />
        <Stat testid="stat-released" icon={Send} label="Released" value={data.released} tone="green" />
        <Stat testid="stat-completed" icon={Users} label="Completed" value={data.completed_students} tone="blue" sub="student attempts" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="ember-card p-6 lg:col-span-2" data-testid="attention-panel">
          <div className="mb-4 flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#FFF1F2] text-[#E11D48]"><AlertTriangle className="h-4 w-4" /></span>
            <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Students needing attention</h2>
          </div>
          {data.attention.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#948EAB]">Everyone is on track. Nice work.</p>
          ) : (
            <div className="divide-y divide-[#F1EEFA]">
              {data.attention.map((s) => (
                <Link key={s.id} to={`/tutor/students/${s.id}`} data-testid={`attention-${s.id}`}
                  className="flex items-center gap-3 py-3 transition-colors hover:bg-[#FAF9FE] rounded-lg px-2 -mx-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={s.avatar_url || undefined} />
                    <AvatarFallback className="bg-[#EDE9FE] text-[#5B21B6] text-xs font-bold">{s.name.split(" ").map((x) => x[0]).join("")}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-[#1E1B2E]">{s.name}</div>
                    <div className="truncate text-xs text-[#948EAB]">{s.program_name}</div>
                  </div>
                  <span className="hidden rounded-full bg-[#FFFBEB] px-2.5 py-1 text-[11px] font-semibold text-[#B45309] sm:inline">{s.reason}</span>
                  <ArrowUpRight className="h-4 w-4 text-[#C4B5FD]" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="ember-card p-6">
          <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Quick actions</h2>
          <div className="mt-4 space-y-3">
            {[
              { to: "/tutor/upload", label: "Upload homework", desc: "Digitise this week's document", icon: Upload },
              { to: "/tutor/homework", label: "Review & release", desc: `${data.awaiting} awaiting approval`, icon: FileClock },
              { to: "/tutor/writing", label: "Writing feedback", desc: "Assess student responses", icon: Send },
              { to: "/tutor/results", label: "Class results", desc: "See performance by section", icon: Users },
            ].map((a) => (
              <Link key={a.to} to={a.to} className="flex items-center gap-3 rounded-xl border border-[#EEEAF8] p-3 transition-all hover:border-[#C4B5FD] hover:bg-[#FAF9FE]">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#EDE9FE] text-[#5B21B6]"><a.icon className="h-4 w-4" /></span>
                <div className="flex-1">
                  <div className="text-sm font-bold text-[#1E1B2E]">{a.label}</div>
                  <div className="text-xs text-[#948EAB]">{a.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
